export type QuestionId =
  | "workType"
  | "humanMembers"
  | "agentMembers"
  | "tasks"
  | "persistence"
  | "runtime"
  | "modelMix"
  | "autonomy"
  | "risk";

export type OptionId =
  | "customer-support"
  | "back-office"
  | "sales-outreach"
  | "it-operations"
  | "custom"
  | "humans-1-10"
  | "humans-11-25"
  | "humans-26-50"
  | "humans-51-100"
  | "humans-101-250"
  | "humans-250-plus"
  | "agents-1-5"
  | "agents-6-15"
  | "agents-16-40"
  | "agents-41-100"
  | "agents-100-plus"
  | "tasks-500"
  | "tasks-2500"
  | "tasks-10000"
  | "tasks-50000"
  | "tasks-150000"
  | "session-memory"
  | "project-memory"
  | "organization-memory"
  | "regulated-memory"
  | "shared-runtime"
  | "standard-runtime"
  | "dedicated-runtime"
  | "regulated-runtime"
  | "efficient-mix"
  | "balanced-mix"
  | "advanced-mix"
  | "frontier-mix"
  | "draft-only"
  | "recommend"
  | "approval-execute"
  | "guarded-execute"
  | "standard-risk"
  | "customer-risk"
  | "financial-risk"
  | "regulated-risk";

export type CalculatorInputs = Partial<Record<QuestionId, OptionId>>;

export type CalculatorOption = {
  id: OptionId;
  label: string;
  description: string;
  compact?: string;
};

export type CalculatorQuestion = {
  id: QuestionId;
  prompt: string;
  helper: string;
  options: CalculatorOption[];
};

export type ToolStatus = "waiting" | "running" | "complete";

export type PricingTool = {
  id: string;
  label: string;
  status: ToolStatus;
  output: string;
};

export type StepStatus = "pending" | "running" | "complete";

export type ProcessStep = {
  label: string;
  status: StepStatus;
};

export type LineItem = {
  label: string;
  detail: string;
  value: string;
};

export type Estimate = {
  answeredCount: number;
  progress: number;
  complete: boolean;
  workTypeLabel: string;
  humanMembers: number;
  agentMembers: number;
  tasksPerMonth: number;
  workUnits: number;
  recommendedPlan: "ONE Pro" | "ONE Business" | "ONE Scale" | "ONE Enterprise";
  monthlyEstimate: number;
  costPerTask: number;
  humanEquivalentFte: number;
  humanAverageCostPerTask: number;
  automationPotential: number;
  annualSavings: number;
  lineItems: LineItem[];
  tools: PricingTool[];
  steps: ProcessStep[];
};

type Profile = {
  label: string;
  multiplier: number;
};

type WorkProfile = Profile & {
  agentIntensity: number;
  minutesPerTask: number;
  automationBase: number;
  complexity: string;
};

type NumericProfile = {
  label: string;
  midpoint: number;
};

type PersistenceProfile = Profile & {
  monthly: number;
  agentMultiplier: number;
};

type RuntimeProfile = Profile & {
  monthly: number;
};

type AutonomyProfile = Profile & {
  agentMultiplier: number;
  automationBoost: number;
};

type RiskProfile = Profile & {
  monthly: number;
};

export const QUESTIONS: CalculatorQuestion[] = [
  {
    id: "workType",
    prompt: "What kind of work is shifting first?",
    helper: "Choose the closest operating pattern. THE SHIFT can refine from there.",
    options: [
      { id: "customer-support", label: "Customer support", description: "Queues, replies, resolutions, summaries." },
      { id: "back-office", label: "Back office processing", description: "Documents, reconciliation, approvals." },
      { id: "sales-outreach", label: "Sales and outreach", description: "Research, qualification, follow-up." },
      { id: "it-operations", label: "IT and operations", description: "Tickets, runbooks, access, incidents." },
      { id: "custom", label: "Other / custom", description: "A mixed or new workflow." }
    ]
  },
  {
    id: "humanMembers",
    prompt: "How many human members will this support?",
    helper: "Use the people who will work with or benefit from the agent workforce.",
    options: [
      { id: "humans-1-10", label: "1 - 10", description: "Small team or pilot" },
      { id: "humans-11-25", label: "11 - 25", description: "One operating pod" },
      { id: "humans-26-50", label: "26 - 50", description: "Department sized" },
      { id: "humans-51-100", label: "51 - 100", description: "Multi-team rollout" },
      { id: "humans-101-250", label: "101 - 250", description: "Large business unit" },
      { id: "humans-250-plus", label: "250+", description: "Enterprise rollout" }
    ]
  },
  {
    id: "agentMembers",
    prompt: "How many agent members should we plan for?",
    helper: "Pick an initial workforce size. THE SHIFT will still recommend capacity from task volume.",
    options: [
      { id: "agents-1-5", label: "1 - 5", description: "Small specialist pod" },
      { id: "agents-6-15", label: "6 - 15", description: "Cross-functional support" },
      { id: "agents-16-40", label: "16 - 40", description: "Department-scale team" },
      { id: "agents-41-100", label: "41 - 100", description: "High-volume workforce" },
      { id: "agents-100-plus", label: "100+", description: "Enterprise digital labor pool" }
    ]
  },
  {
    id: "tasks",
    prompt: "How many repeatable tasks happen each month?",
    helper: "A task is one completed unit of work: a ticket, review, update, handoff, or resolution.",
    options: [
      { id: "tasks-500", label: "Under 1,000", compact: "<1k", description: "Pilot workload" },
      { id: "tasks-2500", label: "1,000 - 5,000", compact: "1k - 5k", description: "Team workflow" },
      { id: "tasks-10000", label: "5,000 - 20,000", compact: "5k - 20k", description: "Department workflow" },
      { id: "tasks-50000", label: "20,000 - 100,000", compact: "20k - 100k", description: "High-volume operation" },
      { id: "tasks-150000", label: "100,000+", compact: "100k+", description: "Enterprise volume" }
    ]
  },
  {
    id: "persistence",
    prompt: "How much memory should the workforce keep?",
    helper: "Storage is included. This prices memory orchestration and governance, not raw storage.",
    options: [
      { id: "session-memory", label: "Session context", description: "Short-lived task memory" },
      { id: "project-memory", label: "Project memory", description: "Project history and preferences" },
      { id: "organization-memory", label: "Organization memory", description: "Shared operating knowledge" },
      { id: "regulated-memory", label: "Regulated evidence memory", description: "Audit-ready retention and replay" }
    ]
  },
  {
    id: "runtime",
    prompt: "Which runtime posture fits the deployment?",
    helper: "Shared works for pilots; dedicated and regulated modes add isolation and controls.",
    options: [
      { id: "shared-runtime", label: "Shared cloud", description: "Best for pilots and light-duty work" },
      { id: "standard-runtime", label: "Standard cloud", description: "Managed production default" },
      { id: "dedicated-runtime", label: "Dedicated cloud", description: "Dedicated capacity and stronger isolation" },
      { id: "regulated-runtime", label: "Regulated runtime", description: "Dedicated controls and stricter gates" }
    ]
  },
  {
    id: "modelMix",
    prompt: "What quality mix should agents use?",
    helper: "The UI uses neutral model classes and avoids provider names.",
    options: [
      { id: "efficient-mix", label: "Efficient", description: "Extraction, routing, simple drafting" },
      { id: "balanced-mix", label: "Balanced", description: "Default quality and throughput mix" },
      { id: "advanced-mix", label: "Advanced", description: "More review, synthesis, planning" },
      { id: "frontier-mix", label: "Frontier ensemble", description: "Complex work and highest reasoning" }
    ]
  },
  {
    id: "autonomy",
    prompt: "How autonomous should the workforce be?",
    helper: "Higher autonomy increases throughput and guardrail work.",
    options: [
      { id: "draft-only", label: "Draft only", description: "Humans execute final actions" },
      { id: "recommend", label: "Recommend next actions", description: "Agents prepare decisions and handoffs" },
      { id: "approval-execute", label: "Execute with approval", description: "Agents act after approval or policy" },
      { id: "guarded-execute", label: "Guarded execution", description: "Agents execute routine work inside limits" }
    ]
  },
  {
    id: "risk",
    prompt: "How sensitive is the work?",
    helper: "Risk posture adds policy checks, evidence, and approval overhead.",
    options: [
      { id: "standard-risk", label: "Standard internal work", description: "Low-risk operations" },
      { id: "customer-risk", label: "Customer data", description: "PII, records, communications" },
      { id: "financial-risk", label: "Financial or operational risk", description: "Money movement, access, SLAs" },
      { id: "regulated-risk", label: "Regulated or audited work", description: "Formal controls and evidence" }
    ]
  }
];

const WORK: Partial<Record<OptionId, WorkProfile>> = {
  "customer-support": { label: "Customer support", multiplier: 0.92, agentIntensity: 0.82, minutesPerTask: 9, automationBase: 0.68, complexity: "queue-heavy" },
  "back-office": { label: "Back office processing", multiplier: 1.08, agentIntensity: 0.94, minutesPerTask: 14, automationBase: 0.63, complexity: "document-heavy" },
  "sales-outreach": { label: "Sales and outreach", multiplier: 1.18, agentIntensity: 1.05, minutesPerTask: 11, automationBase: 0.58, complexity: "research-heavy" },
  "it-operations": { label: "IT and operations", multiplier: 1.24, agentIntensity: 1.12, minutesPerTask: 16, automationBase: 0.56, complexity: "runbook-heavy" },
  custom: { label: "Custom workflow", multiplier: 1.12, agentIntensity: 1, minutesPerTask: 12, automationBase: 0.6, complexity: "mixed" }
};

const HUMANS: Partial<Record<OptionId, NumericProfile>> = {
  "humans-1-10": { label: "1 - 10", midpoint: 6 },
  "humans-11-25": { label: "11 - 25", midpoint: 18 },
  "humans-26-50": { label: "26 - 50", midpoint: 38 },
  "humans-51-100": { label: "51 - 100", midpoint: 76 },
  "humans-101-250": { label: "101 - 250", midpoint: 175 },
  "humans-250-plus": { label: "250+", midpoint: 320 }
};

const AGENTS: Partial<Record<OptionId, NumericProfile>> = {
  "agents-1-5": { label: "1 - 5", midpoint: 3 },
  "agents-6-15": { label: "6 - 15", midpoint: 10 },
  "agents-16-40": { label: "16 - 40", midpoint: 28 },
  "agents-41-100": { label: "41 - 100", midpoint: 70 },
  "agents-100-plus": { label: "100+", midpoint: 140 }
};

const TASKS: Partial<Record<OptionId, NumericProfile>> = {
  "tasks-500": { label: "Under 1,000", midpoint: 650 },
  "tasks-2500": { label: "1,000 - 5,000", midpoint: 2800 },
  "tasks-10000": { label: "5,000 - 20,000", midpoint: 12000 },
  "tasks-50000": { label: "20,000 - 100,000", midpoint: 56000 },
  "tasks-150000": { label: "100,000+", midpoint: 160000 }
};

const PERSISTENCE: Partial<Record<OptionId, PersistenceProfile>> = {
  "session-memory": { label: "Session context", multiplier: 0.92, monthly: 0, agentMultiplier: 0.9 },
  "project-memory": { label: "Project memory", multiplier: 1, monthly: 140, agentMultiplier: 1 },
  "organization-memory": { label: "Organization memory", multiplier: 1.12, monthly: 420, agentMultiplier: 1.08 },
  "regulated-memory": { label: "Regulated evidence memory", multiplier: 1.28, monthly: 1120, agentMultiplier: 1.16 }
};

const RUNTIME: Partial<Record<OptionId, RuntimeProfile>> = {
  "shared-runtime": { label: "Shared cloud", multiplier: 0.88, monthly: 0 },
  "standard-runtime": { label: "Standard cloud", multiplier: 1, monthly: 180 },
  "dedicated-runtime": { label: "Dedicated cloud", multiplier: 1.16, monthly: 780 },
  "regulated-runtime": { label: "Regulated runtime", multiplier: 1.32, monthly: 1800 }
};

const MODEL_MIX: Partial<Record<OptionId, Profile>> = {
  "efficient-mix": { label: "Efficient", multiplier: 0.74 },
  "balanced-mix": { label: "Balanced", multiplier: 1 },
  "advanced-mix": { label: "Advanced", multiplier: 1.34 },
  "frontier-mix": { label: "Frontier ensemble", multiplier: 1.78 }
};

const AUTONOMY: Partial<Record<OptionId, AutonomyProfile>> = {
  "draft-only": { label: "Draft only", multiplier: 0.82, agentMultiplier: 0.78, automationBoost: -0.12 },
  recommend: { label: "Recommend next actions", multiplier: 0.95, agentMultiplier: 0.92, automationBoost: 0 },
  "approval-execute": { label: "Execute with approval", multiplier: 1.12, agentMultiplier: 1.08, automationBoost: 0.08 },
  "guarded-execute": { label: "Guarded execution", multiplier: 1.28, agentMultiplier: 1.18, automationBoost: 0.14 }
};

const RISK: Partial<Record<OptionId, RiskProfile>> = {
  "standard-risk": { label: "Standard internal work", multiplier: 0.95, monthly: 0 },
  "customer-risk": { label: "Customer data", multiplier: 1.12, monthly: 160 },
  "financial-risk": { label: "Financial or operational risk", multiplier: 1.28, monthly: 520 },
  "regulated-risk": { label: "Regulated or audited work", multiplier: 1.55, monthly: 1400 }
};

const PLAN_BASE: Record<Estimate["recommendedPlan"], { monthly: number; workUnits: number }> = {
  "ONE Pro": { monthly: 299, workUnits: 3000 },
  "ONE Business": { monthly: 1290, workUnits: 30000 },
  "ONE Scale": { monthly: 4200, workUnits: 140000 },
  "ONE Enterprise": { monthly: 9500, workUnits: 320000 }
};

export function answeredQuestions(inputs: CalculatorInputs): CalculatorQuestion[] {
  return QUESTIONS.filter((question) => inputs[question.id]);
}

export function currentQuestion(inputs: CalculatorInputs): CalculatorQuestion | undefined {
  return QUESTIONS.find((question) => !inputs[question.id]);
}

export function chooseAnswer(inputs: CalculatorInputs, questionId: QuestionId, optionId: OptionId): CalculatorInputs {
  const index = QUESTIONS.findIndex((question) => question.id === questionId);
  const next: CalculatorInputs = {};

  QUESTIONS.slice(0, index).forEach((question) => {
    if (inputs[question.id]) {
      next[question.id] = inputs[question.id];
    }
  });

  next[questionId] = optionId;
  return next;
}

export function optionFor(questionId: QuestionId, optionId: OptionId | undefined): CalculatorOption | undefined {
  if (!optionId) return undefined;
  return QUESTIONS.find((question) => question.id === questionId)?.options.find((option) => option.id === optionId);
}

export function optionFromText(question: CalculatorQuestion, text: string): OptionId | undefined {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return undefined;

  const exact = question.options.find((option) => option.label.toLowerCase() === normalized);
  if (exact) return exact.id;

  const fuzzy = question.options.find((option) => {
    const label = option.label.toLowerCase();
    const compact = option.compact?.toLowerCase();
    return normalized.includes(label) || Boolean(compact && normalized.includes(compact));
  });
  if (fuzzy) return fuzzy.id;

  const value = Number(normalized.replace(/[^0-9.]/g, ""));
  if (Number.isFinite(value) && value > 0) {
    return numericOption(question.id, normalized.includes("k") ? value * 1000 : value);
  }

  if (question.id === "workType") return "custom";
  return question.options[0]?.id;
}

export function calculate(inputs: CalculatorInputs): Estimate {
  const work = WORK[inputs.workType ?? "customer-support"] ?? WORK["customer-support"]!;
  const humans = HUMANS[inputs.humanMembers ?? "humans-11-25"] ?? HUMANS["humans-11-25"]!;
  const selectedAgents = AGENTS[inputs.agentMembers ?? "agents-6-15"] ?? AGENTS["agents-6-15"]!;
  const tasks = TASKS[inputs.tasks ?? "tasks-2500"] ?? TASKS["tasks-2500"]!;
  const persistence = PERSISTENCE[inputs.persistence ?? "project-memory"] ?? PERSISTENCE["project-memory"]!;
  const runtime = RUNTIME[inputs.runtime ?? "standard-runtime"] ?? RUNTIME["standard-runtime"]!;
  const modelMix = MODEL_MIX[inputs.modelMix ?? "balanced-mix"] ?? MODEL_MIX["balanced-mix"]!;
  const autonomy = AUTONOMY[inputs.autonomy ?? "recommend"] ?? AUTONOMY.recommend!;
  const risk = RISK[inputs.risk ?? "customer-risk"] ?? RISK["customer-risk"]!;

  const answeredCount = answeredQuestions(inputs).length;
  const progress = Math.round((answeredCount / QUESTIONS.length) * 100);
  const workUnits = Math.max(
    100,
    Math.round(tasks.midpoint * work.multiplier * persistence.multiplier * runtime.multiplier * modelMix.multiplier * autonomy.multiplier * risk.multiplier)
  );
  const derivedAgentCapacity = Math.ceil(
    (tasks.midpoint / 760) * work.agentIntensity * persistence.agentMultiplier * autonomy.agentMultiplier * Math.max(0.92, risk.multiplier / 1.18)
  );
  const agentMembers = Math.max(1, Math.max(selectedAgents.midpoint, derivedAgentCapacity));
  const recommendedPlan = recommendPlan(agentMembers, tasks.midpoint, risk.label, workUnits);
  const plan = PLAN_BASE[recommendedPlan];
  const billableWorkUnits = Math.max(0, workUnits - plan.workUnits);
  const humanMemberCost = humans.midpoint * 24;
  const agentMemberCost = agentMembers * 39;
  const workUnitCost = billableWorkUnits * 0.043;
  const monthlyEstimate = Math.round(
    (plan.monthly + humanMemberCost + agentMemberCost + persistence.monthly + runtime.monthly + risk.monthly + workUnitCost) / 10
  ) * 10;
  const humanEquivalentRaw = (tasks.midpoint * work.minutesPerTask) / 9600;
  const automationPotential = clamp(work.automationBase + autonomy.automationBoost - (risk.multiplier > 1.3 ? 0.04 : 0), 0.38, 0.86);
  const humanEquivalentFte = humanEquivalentRaw * automationPotential;
  const humanMonthlyCost = humanEquivalentFte * 6000;
  const humanAverageCostPerTask = humanMonthlyCost / tasks.midpoint;
  const costPerTask = monthlyEstimate / tasks.midpoint;
  const annualSavings = Math.round(Math.max(0, humanMonthlyCost - monthlyEstimate) * 12);

  return {
    answeredCount,
    progress,
    complete: answeredCount === QUESTIONS.length,
    workTypeLabel: work.label,
    humanMembers: humans.midpoint,
    agentMembers,
    tasksPerMonth: tasks.midpoint,
    workUnits,
    recommendedPlan,
    monthlyEstimate,
    costPerTask,
    humanEquivalentFte,
    humanAverageCostPerTask,
    automationPotential,
    annualSavings,
    lineItems: [
      { label: "Human members", value: usd(humanMemberCost), detail: `${humans.label} members at $24/member` },
      { label: "Agent members", value: usd(agentMemberCost), detail: `${agentMembers} recommended at $39/member` },
      { label: "Storage", value: "Included", detail: "Persistent memory storage is free/included" },
      { label: "Persistence", value: usd(persistence.monthly), detail: persistence.label },
      { label: "Runtime", value: usd(runtime.monthly), detail: runtime.label },
      { label: "Model mix", value: `${modelMix.multiplier.toFixed(2)}x`, detail: modelMix.label },
      { label: "Autonomy", value: `${autonomy.multiplier.toFixed(2)}x`, detail: autonomy.label },
      { label: "Risk controls", value: usd(risk.monthly), detail: risk.label },
      { label: "Work units", value: usd(workUnitCost), detail: `${number(workUnits)} units, ${number(billableWorkUnits)} billable after plan allowance` },
      { label: "Recommended plan", value: usd(plan.monthly), detail: recommendedPlan }
    ],
    tools: toolsFor(inputs, {
      humanMembers: humans.midpoint,
      selectedAgentRange: selectedAgents.label,
      agentMembers,
      workUnits,
      recommendedPlan,
      monthlyEstimate,
      costPerTask,
      complexity: work.complexity
    }),
    steps: stepsFor(answeredCount)
  };
}

export function usd(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: fractionDigits
  }).format(value);
}

export function number(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: fractionDigits }).format(value);
}

export function compactUsd(value: number): string {
  if (value >= 1_000_000) return `${usd(value / 1_000_000, 1)}M`;
  if (value >= 1000) return `${usd(value / 1000, 0)}K`;
  return usd(value);
}

function numericOption(questionId: QuestionId, value: number): OptionId | undefined {
  if (questionId === "humanMembers") {
    if (value <= 10) return "humans-1-10";
    if (value <= 25) return "humans-11-25";
    if (value <= 50) return "humans-26-50";
    if (value <= 100) return "humans-51-100";
    if (value <= 250) return "humans-101-250";
    return "humans-250-plus";
  }
  if (questionId === "agentMembers") {
    if (value <= 5) return "agents-1-5";
    if (value <= 15) return "agents-6-15";
    if (value <= 40) return "agents-16-40";
    if (value <= 100) return "agents-41-100";
    return "agents-100-plus";
  }
  if (questionId === "tasks") {
    if (value < 1000) return "tasks-500";
    if (value <= 5000) return "tasks-2500";
    if (value <= 20000) return "tasks-10000";
    if (value <= 100000) return "tasks-50000";
    return "tasks-150000";
  }
  return undefined;
}

function recommendPlan(agentMembers: number, tasksPerMonth: number, riskLabel: string, workUnits: number): Estimate["recommendedPlan"] {
  if (riskLabel.includes("Regulated") || agentMembers > 140 || tasksPerMonth > 140000 || workUnits > 260000) {
    return "ONE Enterprise";
  }
  if (agentMembers > 55 || tasksPerMonth > 45000 || workUnits > 95000) {
    return "ONE Scale";
  }
  if (agentMembers > 12 || tasksPerMonth > 7500 || workUnits > 18000) {
    return "ONE Business";
  }
  return "ONE Pro";
}

function toolsFor(
  inputs: CalculatorInputs,
  values: {
    humanMembers: number;
    selectedAgentRange: string;
    agentMembers: number;
    workUnits: number;
    recommendedPlan: string;
    monthlyEstimate: number;
    costPerTask: number;
    complexity: string;
  }
): PricingTool[] {
  const hasCore = Boolean(inputs.workType && inputs.humanMembers && inputs.agentMembers && inputs.tasks);
  const hasPosture = Boolean(inputs.persistence && inputs.runtime && inputs.modelMix && inputs.autonomy && inputs.risk);

  return [
    {
      id: "human-members",
      label: "pricing.humanMembers",
      status: inputs.humanMembers ? "complete" : "waiting",
      output: inputs.humanMembers ? `${values.humanMembers} human members priced` : "Waiting for team size"
    },
    {
      id: "agent-members",
      label: "pricing.agentMembers",
      status: inputs.agentMembers ? "complete" : inputs.workType ? "running" : "waiting",
      output: inputs.agentMembers ? `${values.agentMembers} agent members from ${values.selectedAgentRange} input and capacity model` : "Waiting for agent-member range"
    },
    {
      id: "tasks",
      label: "pricing.tasks",
      status: inputs.tasks ? "complete" : hasCore ? "running" : "waiting",
      output: inputs.tasks ? `${values.complexity} task pattern included` : "Waiting for task volume"
    },
    {
      id: "work-units",
      label: "pricing.workUnits",
      status: inputs.tasks && inputs.modelMix ? "complete" : inputs.tasks ? "running" : "waiting",
      output: inputs.tasks ? `${number(values.workUnits)} projected monthly work units` : "Waiting for monthly task count"
    },
    {
      id: "posture",
      label: "pricing.posture",
      status: hasPosture ? "complete" : hasCore ? "running" : "waiting",
      output: hasPosture ? "Persistence, runtime, model mix, autonomy, and risk controls priced" : "Waiting for operating posture"
    },
    {
      id: "plan",
      label: "planning.recommendedPlan",
      status: hasCore && hasPosture ? "complete" : hasCore ? "running" : "waiting",
      output: hasCore
        ? `${values.recommendedPlan}, ${usd(values.monthlyEstimate)} / mo, ${usd(values.costPerTask, 2)} per task`
        : "Waiting for required answers"
    }
  ];
}

function stepsFor(answeredCount: number): ProcessStep[] {
  const steps = [
    ["Define shifted work and supported members", 2],
    ["Size the agent workforce and task volume", 4],
    ["Price memory, runtime, quality, autonomy, and risk", 8],
    ["Compute work units and recommended ONE plan", QUESTIONS.length],
    ["Review estimate and capture feedback", QUESTIONS.length]
  ] as const;

  return steps.map(([label, threshold], index) => {
    const previous = steps[index - 1]?.[1] ?? 0;
    if (answeredCount >= threshold) return { label, status: "complete" };
    if (answeredCount >= previous) return { label, status: "running" };
    return { label, status: "pending" };
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
